import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAccountAuthz } from '@/api'
import { useAuth } from '@/hooks/use-auth'
import { useAuthzCatalogue } from '@/hooks/use-authz-catalogue'
import { rightsOf, type Rights } from '@/lib/authz/rights'
import { getApiErrorStatus } from '@/utils/apiError'

export const accountRightsQueryKey = (oid: string | undefined) =>
  ['authz', oid ?? null, 'account'] as const

/**
 * Les droits du membre connecté, agence par agence (TEC-1677). Tant qu'ils manquent, eux ou le catalogue qui
 * dit quoi griser ou cacher (chargement, erreur, gateway sans la route), ou hors mode actif, chaque écran garde
 * sa règle d'avant. Fichier à part de `use-authz` : tous les écrans l'importent, sans la page des rôles.
 */
export const useRights = (): Rights => {
  const { user } = useAuth()
  const oid = user?.organization
  const { data } = useQuery({
    queryKey: accountRightsQueryKey(oid),
    queryFn: async () => (await getAccountAuthz()).data,
    enabled: Boolean(oid),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = getApiErrorStatus(error)
      return (status === undefined || status >= 500) && failureCount < 2
    },
  })
  // Le catalogue ne sert qu'en mode actif : une organisation legacy ne le demande pas.
  const catalogue = useAuthzCatalogue(
    (data?.mode as string | undefined) === 'active'
  )
  return useMemo(() => rightsOf(data, catalogue.data), [data, catalogue.data])
}
